/**
 * App-store port — the store-free seam between shared service code and the
 * Zustand app store (`src/store`).
 *
 * The report and SED pipelines in `src/services` run in two kinds of runtime:
 *
 *   - the browser, where they read and write the live Zustand store;
 *   - Node (Cloud Run worker bundles, the Express server, scripts), where no
 *     client user is ever signed in, so every store action already takes its
 *     "not signed in" branch and the store is only an in-memory container
 *     seeded by `withSeededStoreForJob`.
 *
 * Shared code that the worker bundles reach must not import `src/store` (see
 * `workerBundleBoundary.test.ts`). It calls `loadAppStore()` /
 * `loadAuthMissingProviderNotifier()` instead. The browser entry and the Vitest setup install a
 * loader that returns the real store (`store/installBrowserAppRuntime.ts`);
 * every other runtime falls back to the headless store in
 * `headlessAppStore.ts`, which mirrors the real actions' no-user behaviour.
 *
 * `AppStoreState` lists only the members that store-free code may use. The
 * real `useStore` satisfies it structurally, so adding a member here is a
 * deliberate act that also needs a headless implementation.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteDocument, SiteRagState, UserApiKeys } from '../../types/siteRag';
import type { CompanyArchetype } from '../../types/company';
import type { ReportTemplate } from '../../types/reportTemplate';
import type { SiteComposite } from '../../types/portfolioComposite';

/** The subset of the app store's state and actions available to store-free code. */
export interface AppStoreState {
  siteRag: SiteRagState;
  userApiKeys: UserApiKeys;
  companyId: string | null;
  companyName: string | null;
  companyArchetype: CompanyArchetype | null;
  companyArchetypeNote: string | null;
  companyMissingCriticalCap: number | null;
  companySedBootstrapFreshnessThresholdMs: number | null;
  reportTemplates: ReportTemplate[];
  activeTemplateId: string | null;
  updateSite: (id: string, updates: Partial<SiteEntity>) => Promise<void>;
  assertSiteWritable: (siteId: string) => Promise<void>;
  patchSedRebuildProgressBestEffort: (
    siteId: string,
    progress: NonNullable<SiteEntity['sedRebuildProgress']>,
    opts?: { persist?: boolean },
  ) => Promise<void>;
  patchSiteDocument: (
    siteId: string,
    docId: string,
    patch: Partial<SiteDocument>,
  ) => Promise<void>;
  /**
   * Resolves `null` when the runtime cannot run the client recompute (the
   * headless store); the real store always returns the computed composite.
   */
  computeCompositeForSite: (siteId: string) => Promise<SiteComposite | null>;
}

/**
 * Zustand-compatible handle over {@link AppStoreState}.
 * `setState` writes only {@link AppStoreSeedState}; see that type.
 */
export interface AppStorePort {
  getState(): AppStoreState;
  setState(
    partial: Partial<AppStoreSeedState> | ((state: AppStoreState) => Partial<AppStoreSeedState>),
  ): void;
}

/**
 * The members store-free code may WRITE through the port: the seeded data a
 * worker needs (`withSeededStoreForJob`), never the actions. Typing `setState`
 * against this subset is what keeps the real store assignable to the port:
 * under `strictFunctionTypes` the parameter is checked contravariantly, and
 * the actions differ deliberately (the headless `computeCompositeForSite`
 * resolves `null` where the real store never does).
 */
export type AppStoreSeedState = Pick<AppStoreState,
  | 'siteRag' | 'userApiKeys' | 'companyId' | 'companyName' | 'companyArchetype'
  | 'companyArchetypeNote' | 'companyMissingCriticalCap' | 'companySedBootstrapFreshnessThresholdMs'
  | 'reportTemplates' | 'activeTemplateId'>;

/**
 * The provider-status fields the auth-missing notifier reads. Structural so
 * this module needs no import from `services/dataProviders` (which would
 * close a dependency cycle through `auditTelemetry`); `ProviderStatus`
 * satisfies it.
 */
export interface AuthMissingProviderStatus {
  id: string;
  failureMode?: string;
  envVar?: string;
}

/**
 * Browser: a toast pointing the user at API-key settings when a
 * user-configurable provider failed with `auth-missing`. Headless: no UI.
 *
 * Declared as a method so its parameter is checked bivariantly: the browser
 * implementation (`maybeToastAuthMissingProviders`) is typed against the full
 * `ProviderStatus` and reads only the fields above.
 */
export type AuthMissingProviderNotifier = {
  notify(providerStatuses: readonly AuthMissingProviderStatus[] | undefined, siteId: string): void;
}['notify'];

/**
 * Resolves the live store and client-only helpers. Each member loads only its
 * own module, matching the dynamic imports the call sites used before.
 */
export interface AppRuntimeLoader {
  loadStore: () => Promise<AppStorePort>;
  loadAuthMissingProviderNotifier: () => Promise<AuthMissingProviderNotifier>;
}

// On globalThis rather than module scope so the installed loader survives
// `vi.resetModules()` in tests; the loader itself re-imports the store, so it
// always resolves the current module registry.
const LOADER_KEY = Symbol.for('datacrate.appRuntimeLoader');
type LoaderSlot = { [LOADER_KEY]?: AppRuntimeLoader | null };

function installedLoader(): AppRuntimeLoader | null {
  return (globalThis as LoaderSlot)[LOADER_KEY] ?? null;
}

/** Install (or with `null`, clear) the loader that resolves the live app store. */
export function installAppRuntimeLoader(loader: AppRuntimeLoader | null): void {
  (globalThis as LoaderSlot)[LOADER_KEY] = loader;
}

/** True when a loader for the live store is installed (browser, Vitest). */
export function hasAppRuntimeLoader(): boolean {
  return installedLoader() !== null;
}

/** Resolve the app store handle — the live store in the browser, headless elsewhere. */
export async function loadAppStore(): Promise<AppStorePort> {
  const loader = installedLoader();
  if (loader) return loader.loadStore();
  const { headlessAppStore } = await import('./headlessAppStore');
  return headlessAppStore();
}

/** Resolve the auth-missing notifier — a toast in the browser, a no-op elsewhere. */
export async function loadAuthMissingProviderNotifier(): Promise<AuthMissingProviderNotifier> {
  const loader = installedLoader();
  if (loader) return loader.loadAuthMissingProviderNotifier();
  return () => {
    // No UI headless; the provider statuses stay on the SED as before.
  };
}
